/**
 * Announcements, as a conversation you can read but not write in.
 *
 * A banner across the top is easy to dismiss and then gone forever, so
 * anything posted while somebody was away simply never reached them. As a
 * thread it keeps its history, sits with the rest of their conversations, and
 * is read at whatever moment suits.
 *
 * One-way on purpose: there is nobody on the other end. Rather than offer a
 * message box that quietly goes nowhere, the composer is replaced by a line
 * saying so.
 */

import type { PublicUser } from '@/lib/types';
import { Icon } from '@/icons/Icon';
import { Embed } from './Embed';

/** The reserved name the bot thread answers to. */
export const BOT_USERNAME = '__pogu';

import type { Announcement } from '@/lib/types';
export type { Announcement };

/**
 * A stand-in account for the bot.
 *
 * Not a real row in the database - it never signs in, never joins a party and
 * cannot be messaged, so giving it one would mean guarding every list against
 * an account that is not a person.
 */
export function botUser(): PublicUser {
  return {
    id: BOT_USERNAME,
    username: BOT_USERNAME,
    displayName: 'Pugo',
    role: 'admin',
    banned: null,
    createdAt: 0,
    lastSeen: Date.now(),
    bio: 'Announcements from the team.',
    region: 'global',
    languages: [],
    playstyle: '',
    mic: false,
    games: [],
    badges: [],
    badgeMeta: {},
    showcase: [],
    xp: 0,
    level: 1,
    levelInto: 0,
    levelNeed: 100,
    stats: { hosted: 0, joined: 0, friends: 0, voiceSeconds: 0, messages: 0, lateNight: 0 },
    status: 'online',
    customStatus: '',
    avatar: { seed: BOT_USERNAME, hue: 250, hue2: 265, shape: 1, pattern: 2 },
    avatarUrl: null,
    avatarAnimated: false,
    banner: null,
    bannerAnimated: false,
    bannerColor: null,
    accent: null,
    frame: 'none',
    tier: 'none',
    nameColor: null,
    nameColor2: null,
    nameAnimated: false,
    perks: {
      tier: 'none', staff: true, vip: false, avatarUpload: false, animatedAvatar: false,
      nameColor: false, animatedName: false, banner: false, animatedBanner: false, profileAccent: false,
      // The bot never uploads; the field is here to satisfy the shape.
      limits: { upload: 0, storage: 0, label: 'Bot' }
    }
  };
}

/** The announcements themselves, newest at the bottom like any other chat. */
export function BotThread({ list }: { list: Announcement[] }) {
  if (!list.length) {
    return (
      <div className="grid min-h-0 flex-1 place-items-center px-6 text-center">
        <div>
          <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-[calc(14px*var(--radius-mul,1))] bg-accent-soft text-accent-hi">
            <Icon name="megaphone" size={22} />
          </span>
          <h3 className="text-[15px] font-bold">Nothing announced yet</h3>
          <p className="mt-1 text-[12.5px] text-mute">
            News from the team lands here.
          </p>
        </div>
      </div>
    );
  }

  const ordered = [...list].sort((a, b) => a.createdAt - b.createdAt);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-[var(--pad)] py-[var(--gap)]">
      <div className="flex w-full max-w-[720px] flex-col gap-3">
        {ordered.map((a) => <Embed key={a.id} item={a} />)}
      </div>
    </div>
  );
}

/** What sits where the message box would be. */
export function BotComposer() {
  return (
    <div className="flex-none px-[var(--pad)] pb-[var(--gap)]">
      <div className="flex items-center justify-center gap-2 rounded-[var(--radius)] border border-line-soft bg-surface-1 px-4 py-3 text-[12.5px] text-mute">
        <Icon name="lock" size={14} className="flex-none opacity-70" />
        You cannot reply to announcements.
      </div>
    </div>
  );
}
